import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {AbstractGuard} from '../abstracts/guard.abstract.';
import {AuthenticationService} from '../authentication/authentication.service';
import {ToastService} from '../notification/toast/toast.service';
import {CrudService} from '../services/crud.service';
import {PerfilTipoEnum} from '../../shared/enums/perfil-tipo.enum';
import {PessoaTipoEnum} from '../../shared/enums/pessoa-tipo.enum';

@Injectable({
  providedIn: 'root'
})
export class EmpresaProfileCompletionGuard extends AbstractGuard {
  private readonly companyProfileRoute = '/configuracoes-empresa';

  constructor(private authenticationService: AuthenticationService,
              private crudService: CrudService,
              private toastService: ToastService,
              private router: Router) {
    super();
  }

  guard(activatedRouter: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> | boolean {
    if (this.authenticationService.getProfileType() !== PerfilTipoEnum.EMPRESA) {
      return true;
    }

    const currentUrl = (routerState.url || '').split('?')[0];
    if (currentUrl === this.companyProfileRoute) {
      return true;
    }

    return this.crudService.get('empresas-profile', null, false).pipe(
      map((empresa: any) => {
        const missingFields = this.getIncompleteProfileFields(empresa);
        if (missingFields.length === 0) {
          return true;
        }

        this.toastService.sendWarning(this.buildProfileWarningMessage(missingFields), 'Complete o perfil da empresa', true, 8000);
        this.router.navigate([this.companyProfileRoute]);
        return false;
      }),
      catchError(() => of(true))
    );
  }

  private getIncompleteProfileFields(empresa: any): string[] {
    if (!empresa) {
      return ['os dados da empresa'];
    }

    const tipo = empresa.tipo;
    const contatos = empresa.contatos || {};
    const endereco = empresa.endereco || {};
    const missingFields: string[] = [];

    if (!this.hasText(tipo)) {
      missingFields.push('tipo de pessoa');
    }

    if (!this.hasText(empresa.nome) || empresa.nome.trim().length < 5) {
      missingFields.push(tipo === PessoaTipoEnum.PJ ? 'nome da empresa' : 'nome completo');
    }

    if (tipo === PessoaTipoEnum.PF) {
      if (!this.hasText(empresa.cpf)) {
        missingFields.push('CPF');
      }
    } else if (tipo === PessoaTipoEnum.PJ) {
      if (!this.hasText(empresa.cnpj)) {
        missingFields.push('CNPJ');
      }
    } else {
      missingFields.push('CPF/CNPJ');
    }

    if (!this.hasText(endereco.zipCode)) {
      missingFields.push('CEP');
    }
    if (!this.hasText(endereco.street)) {
      missingFields.push('rua');
    }
    if (!this.hasText(endereco.number)) {
      missingFields.push('número do endereço');
    }
    if (!this.hasText(endereco.neighborhood)) {
      missingFields.push('bairro');
    }
    if (!this.hasAddressCity(endereco)) {
      missingFields.push('cidade');
    }

    const hasPhone = Array.isArray(contatos.telefones)
      && contatos.telefones.some(phone => this.hasText(phone));
    if (!hasPhone) {
      missingFields.push('telefone');
    }

    return missingFields;
  }

  private buildProfileWarningMessage(missingFields: string[]): string {
    const pendingLabel = missingFields.length === 1 ? 'Campo pendente' : 'Campos pendentes';
    return `Atualize o perfil da empresa para continuar no portal. ${pendingLabel}: ${this.formatMissingFields(missingFields)}.`;
  }

  private formatMissingFields(missingFields: string[]): string {
    if (missingFields.length === 1) {
      return missingFields[0];
    }

    if (missingFields.length === 2) {
      return `${missingFields[0]} e ${missingFields[1]}`;
    }

    return `${missingFields.slice(0, -1).join(', ')} e ${missingFields[missingFields.length - 1]}`;
  }

  private hasText(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim().length > 0;
  }

  private hasAddressCity(endereco: any): boolean {
    const city = endereco?.city ?? endereco?.cidade;

    return this.hasText(endereco?.cityId)
      || this.hasText(endereco?.cityName)
      || this.hasText(endereco?.cidadeId)
      || this.hasText(endereco?.cidadeNome)
      || this.hasText(typeof city === 'string' ? city : null)
      || this.hasText(city?.id)
      || this.hasText(city?.name)
      || this.hasText(city?.nome);
  }
}
